import {
  IonButton,
  IonCheckbox,
  IonCol,
  IonGrid,
  IonInput,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonRow
} from '@ionic/react';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import DateInput from '../../components/DateInput';
import { ErrorMessage } from '@hookform/error-message';
import FormError from '../../components/FormError';
import './CheckInForm.scss';
import { AuthenticationRootState } from '../../store/reducers/authentication';
import { AppointmentsRootState } from '../../store/reducers/appointments';
import { setOptionalFormData } from '../../store/actions/appointments';
import { connect } from 'react-redux';
import { STRINGS } from '../../i18n/strings';
import Field from '../../components/Field';
import { dateValidator } from '../../utils/validators';
import moment from 'moment';

export const optionalFormName = 'hormonology';

type Inputs = {
  ddr?: Date | null;
  'jour-du-cycle'?: number | null;
  'jour-de-la-stimulation'?: number | null;
  'traitements-en-cours'?: string[];
  'traitements-en-cours-autre'?: string;
  'examens-a-realiser'?: string[];
  'contexte-clinique'?: string[];
  'contexte-clinique-date'?: Date | null;
  'date-naissance-conjoint'?: Date | null;
};

const mapStateToProps = (
  state: {
    authentication: AuthenticationRootState;
    appointments: AppointmentsRootState;
  },
  ownProps: any
) => {
  return {
    formData: state.appointments.optionalForms[optionalFormName] || []
  };
};

export type OptionalFormData = {
  key: string;
  label: string;
  value: any;
}[];

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  setOptionalFormData: ({ data }: { data: OptionalFormData }) =>
    dispatch(setOptionalFormData(optionalFormName, data))
});

const FormHormonologie: React.FC<
  ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> & {
      onSuccess: (key: string, data: OptionalFormData) => void;
    }
> = ({ formData, onSuccess = () => {} }) => {
  const { control, handleSubmit, errors, getValues } = useForm<Inputs>({
    defaultValues: {
      ddr: null,
      'jour-du-cycle': null,
      'contexte-clinique': [],
      'contexte-clinique-date': null,
      'examens-a-realiser': [],
      'jour-de-la-stimulation': null,
      'traitements-en-cours': [],
      'traitements-en-cours-autre': '',
      'date-naissance-conjoint': null,
      ...Object.fromEntries(formData.map((data) => [data.key, data.value]))
    }
  });

  const labels = {
    ddr: 'DDR',
    'jour-du-cycle': 'Jour du cycle',
    'contexte-clinique': 'Contexte clinique',
    'contexte-clinique-date': 'Date Contexte Clinique',
    'examens-a-realiser': 'Examens à réaliser',
    'jour-de-la-stimulation': 'Jour de la stimulation',
    'traitements-en-cours': 'Traitements en cours',
    'traitements-en-cours-autre': 'Autre traitements en cours',
    'date-naissance-conjoint': 'Date de naissance du conjoint'
  };

  function showError(fieldName: keyof Inputs) {
    return (
      (errors as any)[fieldName] && (
        <ErrorMessage
          errors={errors}
          name={fieldName}
          render={({ message }) => <FormError message={message as string} />}
        />
      )
    );
  }

  const traitementsEnCours = [
    'Bemfola',
    'Estima G',
    'Menopur',
    'Progynova',
    'Cetrotide',
    'Fertistart',
    'Norprolac',
    'Provames',
    'Clomid',
    'Fostimon',
    'Orgalutran',
    'Puregon',
    'Décapeptyl',
    'Gonal F',
    'Ovitrelle',
    'Suprefact',
    'Dostinex',
    'Gonapeptyl',
    'Parlodel',
    'Synarel',
    'Duphaston',
    'HCG chorionique',
    'Pergotime',
    'Utrogestan',
    'Elonva',
    'Lutrelef',
    'Pergoveris',
    'Viveldot',
    'Enantone',
    'Luveris',
    'Progestérone',
    'Aucun traitement'
  ];

  const examensARealiser = [
    { value: 'OE2', label: 'OE2<br/><small>œstrogène</small>' },
    { value: 'LH', label: 'LH<br/><small>hormone lutéinisante</small>' },
    { value: 'PRG', label: 'PRG<br/><small>Progestérone</small>' },
    {
      value: 'HCB',
      label: 'HCB<br/><small>hormone chorionique gonadotrope</small>'
    }
  ];

  const contexteClinique = ['FIV', 'ISCI', 'TEC (transfert embryons congelés)'];

  function onSubmit(values: Inputs) {
    let data = Object.entries(values).map(([key, value]) => {
      let v = value;
      const date = moment(v, 'DD/MM/YYYY');
      if (date.isValid()) {
        // NOTE: It seems that the server doesn't parse it like Javascript does, it removes one day, so the add it manually
        date.add(1, 'day');
        v = date.toISOString();
      }
      return {
        key,
        label: labels[key as keyof Inputs],
        value: v
      };
    });

    data = data.filter(
      (data) => data.value !== null && typeof data.value !== 'undefined'
    );

    onSuccess(optionalFormName, data);
  }

  return (
    <div className="c-checking-form p-form-hormonologie wrapper wrapper--large wrapper--stretch wrapper--centered-md ion-margin-top">
      <header className="form-header">
        <h3 className="form-title">Hormonologie</h3>
        <p className="form-description">
          Prélèvement : fiches de renseignements cliniques
        </p>
      </header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <IonGrid>
          <IonRow>
            <IonCol size="12" sizeXl="10">
              <IonLabel className="form-label">
                Date des dernières règles
              </IonLabel>
              <Controller
                control={control}
                name="ddr"
                rules={{
                  validate: {
                    onlyOne: (value) =>
                      [
                        value,
                        getValues('jour-du-cycle'),
                        getValues('jour-de-la-stimulation')
                      ].filter((x) => !!x).length <= 1 ||
                      STRINGS.FR.FORM_ERROR_MAX_ONE,
                    ...dateValidator.validate
                  }
                }}
                render={({ ref, ...props }) => (
                  <DateInput {...props} labelClassname="form-label" />
                )}
              />
              {showError('ddr')}
            </IonCol>
          </IonRow>
          <IonRow className="ion-margin-top">
            <IonCol size="12" sizeXl="5">
              <IonLabel className="form-label">
                <span className="form-or">ou</span> Jour du cycle
              </IonLabel>
              <Controller
                control={control}
                name="jour-du-cycle"
                rules={{
                  validate: {
                    onlyOne: (value) =>
                      [
                        value,
                        getValues('ddr'),
                        getValues('jour-de-la-stimulation')
                      ].filter((x) => !!x).length <= 1 ||
                      STRINGS.FR.FORM_ERROR_MAX_ONE
                  }
                }}
                render={(props) => (
                  <IonInput
                    {...props}
                    onIonChange={(e) => {
                      props.onChange(e.detail.value!);
                    }}
                    placeholder="De 1 à 28"
                    type="number"
                    min="1"
                    max="28"
                  />
                )}
              />
              {showError('jour-du-cycle')}
            </IonCol>
            <IonCol size="12" sizeXl="5">
              <IonLabel className="form-label">
                <span className="form-or">ou</span> Jour de la stimulation
              </IonLabel>
              <Controller
                control={control}
                name="jour-de-la-stimulation"
                rules={{
                  validate: {
                    onlyOne: (value) =>
                      [
                        value,
                        getValues('jour-du-cycle'),
                        getValues('ddr')
                      ].filter((x) => !!x).length <= 1 ||
                      STRINGS.FR.FORM_ERROR_MAX_ONE
                  }
                }}
                render={(props) => (
                  <IonInput
                    {...props}
                    onIonChange={(e) => {
                      props.onChange(e.detail.value!);
                    }}
                    placeholder="De 1 à 20"
                    type="number"
                    min="1"
                    max="20"
                  />
                )}
              />
              {showError('jour-de-la-stimulation')}
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonLabel className="form-label form-label--separator">
                Traitement en cours
              </IonLabel>
              <Controller
                control={control}
                name="traitements-en-cours"
                render={({ onChange, ...props }) => (
                  <IonGrid>
                    <IonRow>
                      {traitementsEnCours.map((val, i) => (
                        <IonCol key={i} size="6" sizeXl="4">
                          <IonItem lines="none">
                            <IonCheckbox
                              {...props}
                              value={val}
                              onIonChange={(e) => {
                                let values: string[] =
                                  getValues(props.name) || [];

                                if (e.detail.checked) {
                                  values.push(e.detail.value);
                                } else {
                                  values = values.filter(
                                    (value) => value !== e.detail.value
                                  );
                                }

                                onChange(values);
                              }}
                              slot="start"
                              defaultChecked={props.value?.includes(val)}
                            />
                            <IonLabel>{val}</IonLabel>
                          </IonItem>
                        </IonCol>
                      ))}
                    </IonRow>
                  </IonGrid>
                )}
              />
              {showError('traitements-en-cours')}
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonLabel className="form-label">Autre :</IonLabel>
              <Controller
                control={control}
                name="traitements-en-cours-autre"
                render={(props) => (
                  <IonInput
                    {...props}
                    onIonChange={(e) => {
                      props.onChange(e.detail.value!);
                    }}
                    placeholder="Autre traitement"
                  />
                )}
              />
              {showError('traitements-en-cours-autre')}
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonLabel className="form-label form-label--separator">
                Traitement à réaliser
              </IonLabel>
              <Controller
                control={control}
                name="examens-a-realiser"
                render={({ onChange, ...props }) => (
                  <IonGrid>
                    <IonRow>
                      {examensARealiser.map((val, i) => (
                        <IonCol key={i} size="6">
                          <IonItem lines="none">
                            <IonCheckbox
                              {...props}
                              value={val.value}
                              onIonChange={(e) => {
                                let values: string[] =
                                  getValues(props.name) || [];

                                if (e.detail.checked) {
                                  values.push(e.detail.value);
                                } else {
                                  values = values.filter(
                                    (value) => value !== e.detail.value
                                  );
                                }

                                onChange(values);
                              }}
                              defaultChecked={props.value?.includes(val)}
                              slot="start"
                            />
                            <IonLabel>
                              <span
                                dangerouslySetInnerHTML={{ __html: val.label }}
                              ></span>
                            </IonLabel>
                          </IonItem>
                        </IonCol>
                      ))}
                    </IonRow>
                  </IonGrid>
                )}
              />
              {showError('examens-a-realiser')}
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonLabel className="form-label form-label--separator">
                Contexte clinique
              </IonLabel>
              <Controller
                control={control}
                name="contexte-clinique"
                render={({ onChange, ...props }) => (
                  <IonGrid>
                    <IonRadioGroup
                      onIonChange={(e) => {
                        onChange([e.detail.value]);
                      }}
                    >
                      <IonRow>
                        <IonCol size="12">
                          <IonItem lines="none">
                            <IonRadio
                              {...props}
                              slot="start"
                              value="Insémination Intra Utérine (IIU)"
                              defaultChecked={props.value?.includes(
                                'Insémination Intra Utérine (IIU)'
                              )}
                            />
                            <IonLabel>
                              Insémination Intra Utérine (IIU)
                            </IonLabel>
                          </IonItem>
                        </IonCol>
                        {contexteClinique.map((val, i) => (
                          <IonCol key={i} size="12">
                            <IonItem lines="none">
                              <IonRadio
                                {...props}
                                value={val}
                                slot="start"
                                defaultChecked={props.value?.includes(val)}
                              />
                              <IonLabel>{val}</IonLabel>
                            </IonItem>
                          </IonCol>
                        ))}
                      </IonRow>
                    </IonRadioGroup>
                  </IonGrid>
                )}
              />
              {showError('contexte-clinique')}
            </IonCol>
          </IonRow>
          <IonRow>
            <Field
              type="datetime-local"
              controllerProps={{
                control,
                name: 'contexte-clinique-date'
              }}
              errors={errors}
              label="Date de l'intervention"
            />
          </IonRow>
          <IonRow>
            <Field
              type="datetime-local"
              controllerProps={{
                control,
                name: 'date-naissance-conjoint'
              }}
              errors={errors}
              label={labels['date-naissance-conjoint']}
            />
          </IonRow>
        </IonGrid>
        <div className="ion-text-center ion-margin-top">
          <IonButton type="submit" expand="block">
            Suivant
          </IonButton>
        </div>
      </form>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(FormHormonologie);
